import { useState } from "react";
import { useAuth } from "../../context";
import useFetchObjects from "../../api/useFetchObjects";
import useDebounce from "../../utils/useDebounce";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";

const DEBOUNCE_MS = 400;
const MIN_CHARS = 3;

/**
 * TicketSearch Component
 * Reusable component for searching visit tickets across all modules.
 * Uses debounced search (400ms) to reduce API calls.
 *
 * Usage:
 * <TicketSearch
 *   value={selectedTicket}
 *   onChange={(ticket) => setSelectedTicket(ticket)}
 *   placeholder="Search by visit ticket, MRN, or patient name..."
 * />
 */
export default function TicketSearch({ value, onChange, placeholder = "Search by visit ticket, MRN, or patient name...", label = "Visit Ticket", size = "medium", fullWidth = true }) {
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [inputValue, setInputValue] = useState("");

  const debouncedSearch = useDebounce(inputValue.trim(), DEBOUNCE_MS);
  const shouldFetch = debouncedSearch.length >= MIN_CHARS;

  const { data: ticketsResponse, isLoading } = useFetchObjects(
    shouldFetch ? ["tickets", "search", debouncedSearch] : null,
    shouldFetch ? `tickets/?search=${encodeURIComponent(debouncedSearch)}` : "",
    token,
    { enabled: shouldFetch }
  );

  const tickets = Array.isArray(ticketsResponse)
    ? ticketsResponse
    : ticketsResponse?.results ?? [];

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      options={tickets}
      getOptionLabel={(option) => option?.ticket_number || ""}
      value={value}
      onChange={(e, newValue) => onChange(newValue)}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      onInputChange={(e, newInputValue) => setInputValue(newInputValue)}
      inputValue={inputValue}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body1" fontWeight="bold">
              {option.ticket_number}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {option.patient_name_display} {option.patient_father_name_display ? `- ${option.patient_father_name_display}` : ""}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {option.patient_mrn_display ? `MRN: ${option.patient_mrn_display}` : "No MRN"} · {option.patient_phone_display || option.patient_phone || "No phone"}
            </Typography>
          </Box>
        </Box>
      )}
      noOptionsText={inputValue.length < MIN_CHARS ? `Type at least ${MIN_CHARS} characters to search` : "No tickets found"}
    />
  );
}
